/* eslint-disable camelcase */
import {
  CheckCircleTwoTone,
  ControlOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { Badge, Button, Col, DatePicker, Dropdown, Form, Menu, Radio, Row, Select, Table, Tooltip } from 'antd'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
//
import {
  addOrUpdateRemoteWork,
  deleteRemoteWork,
  fetchRemoteWorks,
  fetchStaticItem,
  handleRemoteWorkActions,
} from 'appRedux/actions'
import EmployeeCell from 'components/EmployeeCell'
import FormModal from 'components/RemoteWorkFormModal'
import ViewModal from 'components/RemoteWorkViewModal'
import { EmployeeSearchBox } from 'components/SearchBox'
import Widget from 'components/Widget'
import { RemoteWorkService } from 'services'
import { REST_API } from 'services/rest.api'
import Auxiliary from 'util/Auxiliary'
import {
  DATE_FORMAT,
  getDateFormat,
  getTimeFormat,
  PAGINATION_LIMIT,
  PAGINATION_TYPES,
  STATUS_BG,
  TIME_FORMAT,
} from 'util/constants'
import {
  getInitialSearch,
  setSearchInLocalStorage,
  showDeleteConfirm,
  sortColumnObject,
} from 'util/helpers'

const actions = {
  SUBMIT: '/submit',
  APPROVE: '/approve',
  REJECT: '/reject',
}

const { RangePicker } = DatePicker
const dateFormat = 'YYYY-MM-DD'

const initialSearch = {
  employee_id: '',
  type_id: '',
  from_date_range: [],
  to_date_range: [],
}

const RemoteWork = () => {
  const REMOTEListType = localStorage.getItem('REMOTE_LIST_TYPE') ?? 'self'
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { remoteWorks, allfieldsData, isLoading } = useSelector(({ pagination, common, loading }) => ({
    remoteWorks: pagination[PAGINATION_TYPES.remote_work],
    allfieldsData: common[REST_API.remote_work_purpose_type_activity_transport],
    isLoading: loading[PAGINATION_TYPES.leave] || loading[PAGINATION_TYPES.remote_work],
    height: common.height, // TODO it will be use in table
  }))
  const [listType, setListType] = useState(REMOTEListType)
  const { url } = useRouteMatch()
  const [search, setSearch] = useState(getInitialSearch(initialSearch, url))
  const [visible, setVisible] = useState(false)
  const [editItem, setEditItem] = useState(undefined)
  const [viewVisible, setViewVisible] = useState(false)
  const [viewItem, setViewItem] = useState(null)
  const [current, setCurrent] = useState(1)
  const [sort, setSort] = useState(JSON.parse(localStorage.getItem(`${url}/sort`)) || '')
  const handleInitial = useCallback(
    (_page, limit = PAGINATION_LIMIT) => {
      setCurrent(_page)
      const { employee_id, ...rest } = search || {}
      const query = {
        sort: 'id,desc',
        limit,
        ...rest,
        page: _page,
        list_type: listType,
        ...(sort && { sort }),
        ...(listType !== 'self' && { employee_id }),
      }
      dispatch(fetchRemoteWorks(query))
    },
    [dispatch, listType, search, sort]
  )

  useEffect(() => {
    if (search) {
      setSearchInLocalStorage(search, url)
    }
  }, [search, url])

  useEffect(() => {
    if (sort) {
      localStorage.setItem(`${url}/sort`, JSON.stringify(sort))
    }
  }, [sort])

  useEffect(() => {
    handleInitial(1)
  }, [handleInitial])

  useEffect(() => {
    dispatch(fetchStaticItem(REST_API.remote_work_purpose_type_activity_transport, RemoteWorkService))
  }, [dispatch])

  const data = useMemo(() => remoteWorks?.items ?? [], [remoteWorks])
  const pagination = {
    current,
    hideOnSinglePage: true,
    position: 'both',
    total: remoteWorks.total,
    pageSizeOptions: [10, 20, 50],
    showQuickJumper: true,
    onChange: (page, pageSize) => handleInitial(page, pageSize),
  }

  const handleEdit = (item) => {
    const { type, from_time, to_time, to_date, from_date, purpose, tour_plans, ...rest } = item
    const formValues = {
      ...rest,
      type_id: type.id,
      from_date: getDateFormat(from_date),
      from: type.id === 1 ? getTimeFormat(from_time) : getDateFormat(from_date),
      to: type.id === 1 ? getTimeFormat(to_time) : getDateFormat(to_date),
    }

    if (type.id === 3) {
      formValues.from_date = getDateFormat(from_date)
      formValues.to_date = getDateFormat(to_date)
      if (purpose?.id) {
        formValues.tour_purpose_id = purpose.id
      }
      if (Array.isArray(tour_plans) && tour_plans.length > 0) {
        tour_plans.forEach((plan, idx) => {
          const index = idx + 1
          const dateYMD = moment(plan.date).format('YYYY-MM-DD')
          formValues[`day_${index}_${dateYMD}_date`] = moment(plan.date).format('DD-MM-YYYY')
          formValues[`day_${index}_${dateYMD}_id`] = plan.id
          formValues[`day_${index}_${dateYMD}_location`] = plan.location
          formValues[`day_${index}_${dateYMD}_planned_activity`] = (plan.planned_activities || []).map((a) => a.id)
          formValues[`day_${index}_${dateYMD}_transport`] = plan.transport?.id
          formValues[`day_${index}_${dateYMD}_description`] = plan.note
        })
      }
    }

    form.setFieldsValue(formValues)
    setVisible(true)
    setEditItem(item)
  }

  // Delete from View modal: close modal on success
  const handleViewDelete = (item) => {
    showDeleteConfirm(<ExclamationCircleOutlined />, 'remote work', async () => {
      const res = await dispatch(deleteRemoteWork(item.id))
      if (res?.success) {
        setViewVisible(false)
      }
      return res
    })
  }

  const handleView = (item) => {
    setViewItem(item)
    setViewVisible(true)
  }

  const handleChange = (value = '', key) => {
    setSearch((prevState) => ({ ...prevState, [key]: value }))
  }

  const handleDateChange = (dates, key) => {
    if (!dates?.length) {
      handleChange([], key)
      return
    }
    handleChange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')], key)
  }

  const handleDelete = (item) => {
    showDeleteConfirm(<ExclamationCircleOutlined />, 'remote work', () => {
      return dispatch(deleteRemoteWork(item.id))
    })
  }
  const handleActions = async (item, TYPE) => {
    const values = {
      key: PAGINATION_TYPES.remote_work,
      route: actions[TYPE],
      id: item.id,
    }
    const res = await dispatch(handleRemoteWorkActions(values))
    // refresh current page to reflect latest status (optional; list already updated by action)
    // handleInitial(current)
    if (res) {
      setViewItem(res)
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      ...sortColumnObject('id', setSort, sort),
    },
    {
      title: 'Employee',
      dataIndex: 'employee',
      width: 180,
      render: (_) => {
        const { name, photo, eid } = _ ?? { name: '', photo: { small: 'https//' } }
        return <EmployeeCell {...{ name, photo: photo?.small, eid }} />
      },
      ...sortColumnObject('employee_id', setSort, sort),
    },
    {
      title: 'Line Manager',
      dataIndex: 'line_manager',
      width: 180,
      render: (_) => {
        const { name, photo, eid } = _?.employee ?? { name: '', photo: { small: 'https//' } }
        if (!_?.employee) {
          return (
            <div className="gx-mb-0 gx-ml-1">
              <h4 className="gx-mb-0">{_?.name}</h4>
            </div>
          )
        }
        return <EmployeeCell {...{ name, photo: photo?.small, eid }} />
      },
      ...sortColumnObject('line_manager_id', setSort, sort),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 120,
      render: (_) => _?.name,
      ...sortColumnObject('type_id', setSort, sort),
    },
    {
      title: 'From',
      dataIndex: 'from',
      width: 115,
      // eslint-disable-next-line react/display-name
      render: (_, record) => {
        const formatDate =
          record?.type?.id !== 1
            ? moment(record?.from_date).format('DD-MM-YYYY')
            : `${moment(record?.from_date).format('DD-MM-YYYY')}(${moment(record?.from_time).format('HH:mm')})`
        return <span>{formatDate}</span>
      },
      ...sortColumnObject('from', setSort, sort),
    },
    {
      title: 'To',
      dataIndex: 'to',
      width: 115,
      // eslint-disable-next-line react/display-name
      render: (_, record) => {
        const date =
          record?.type?.id !== 1
            ? moment(record?.to_date).format('DD-MM-YYYY')
            : moment(record?.to_time).format('HH:mm')
        return <span>{date}</span>
      },
      ...sortColumnObject('to_date', setSort, sort),
    },
    {
      title: 'Created At',
      dataIndex: 'created',
      width: 175,
      render: (_) => (<span>{moment(_).format('DD-MM-YYYY - HH:mm')}</span>),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated',
      width: 175,
      render: (_) => (<span>{moment(_).format('DD-MM-YYYY - HH:mm')}</span>),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 115,
      // eslint-disable-next-line react/display-name
      render: (_) => (
        <Badge
          className="site-badge-count-109"
          count={_?.name}
          style={{ backgroundColor: STATUS_BG[_?.id] }}
        />
      ),
      ...sortColumnObject('status_id', setSort, sort),
    },
    {
      title: 'Action',
      width: 100,
      key: 'action',
      fixed: 'right',
      // eslint-disable-next-line react/display-name
      render: (_, record) => {
        const statusID = record?.status?.id
        const menu = (
          <Menu>
            <Menu.Item key="edit" disabled={statusID !== 1} onClick={() => handleEdit(record)}>
              <EditOutlined />
              EDIT
            </Menu.Item>
            <Menu.Item key="delete" disabled={statusID !== 1} onClick={() => handleDelete(record)}>
              <DeleteOutlined />
              DELETE
            </Menu.Item>
            <Menu.Item
              key="submit"
              disabled={statusID !== 1}
              onClick={() => handleActions(record, 'SUBMIT')}
            >
              <CheckCircleTwoTone twoToneColor={STATUS_BG[2]} />
              SUBMIT
            </Menu.Item>
            <Menu.Item
              key="approve"
              disabled={statusID !== 2 || listType === 'self'}
              onClick={() => handleActions(record, 'APPROVE')}
            >
              <CheckCircleTwoTone twoToneColor={STATUS_BG[3]} />
              APPROVE
            </Menu.Item>
            <Menu.Item
              key="reject"
              disabled={statusID !== 2 || listType === 'self'}
              onClick={() => handleActions(record, 'REJECT')}
            >
              <CheckCircleTwoTone twoToneColor={STATUS_BG[4]} />
              REJECT
            </Menu.Item>
            {record?.type?.id === 3 && (
              <Menu.Item
                key="view"
                onClick={() => handleView(record)}
              >
                <EyeOutlined />
                VIEW
              </Menu.Item>
            )}
          </Menu>
        )
        return (
          <Dropdown
            arrow
            overlay={menu}
            placement="bottomCenter"
          >
            <ControlOutlined />
          </Dropdown>
        )
      },
    },
  ]

  const renderData = () => (
    <Row justify="center" align="middle">
      <Col span={5}>
        <Tooltip title="Create Remote Work Application">
          <Button
            shape="circle"
            type="primary"
            className="gx-mt-3"
            onClick={() => {
              setEditItem(null)
              setVisible(true)
            }}
          >
            <span className="gx-fs-xxl">+</span>
          </Button>
        </Tooltip>
        <span>
          <Radio.Group
            options={[
              { label: 'Self', value: 'self' },
              { label: 'Team', value: 'team' },
            ]}
            onChange={(e) => {
              setListType(e?.target?.value)
              localStorage.setItem('REMOTE_LIST_TYPE', e?.target?.value)
            }}
            value={listType}
            optionType="button"
            buttonStyle="solid"
          />
        </span>
      </Col>
      <Col span={5}>
        <EmployeeSearchBox
          searchFilter
          value={search?.employee_id}
          disabled={listType === 'self'}
          onChange={(item) => handleChange(item?.value?.split('#')[0], 'employee_id')}
        />
      </Col>
      <Col span={4}>
        <Select
          allowClear
          className="gx-w-100"
          placeholder="Select type"
          onChange={(item) => handleChange(item, 'type_id')}
          defaultValue={search?.type_id === '' ? null : search?.type_id}
          options={allfieldsData?.type?.map(i => ({ value: i.id, label: i.name }))}
        />
      </Col>
      <Col span={5}>
        <RangePicker
          ranges={{
            Today: [moment(), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          placeholder={['From start', 'From end']}
          format={DATE_FORMAT}
          onChange={(v) => handleDateChange(v, 'from_date_range')}
          defaultValue={search?.from_date_range?.length > 0 && [moment(search?.from_date_range[0], dateFormat), moment(search?.from_date_range[1], dateFormat)]}
        />
      </Col>
      <Col span={5}>
        <RangePicker
          ranges={{
            Today: [moment(), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          placeholder={['To start', 'To end']}
          format={DATE_FORMAT}
          onChange={(v) => handleDateChange(v, 'to_date_range')}
          defaultValue={search?.to_date_range?.length > 0 && [moment(search?.to_date_range[0], dateFormat), moment(search?.to_date_range[1], dateFormat)]}
        />
      </Col>
      <Col span={24} className="gx-mt-4">
        <Table
          loading={isLoading}
          columns={columns}
          dataSource={data}
          pagination={pagination}
          rowKey={(item) => item.id}
          scroll={{ x: 1000 }}
        />
      </Col>
    </Row>
  )

  return (
    <Auxiliary>
      <Widget>{renderData()}</Widget>
      {visible && (
        <FormModal
          {...{
            visible,
            formName: 'remote-work-create-form',
            handleCancel: () => setVisible(false),
            handleOk: (fieldsValue) => {
              console.log(fieldsValue)
              const { type_id, activity_type } = fieldsValue
              const values = { ...fieldsValue }
              if (type_id === 1) {
                // out of office id  = 1
                values.from_date = fieldsValue.from_date.format(DATE_FORMAT)
                values.to_date = fieldsValue.from_date.format(DATE_FORMAT)
                values.from_time = `${fieldsValue.from.format(TIME_FORMAT)}:00`
                values.to_time = `${fieldsValue.to.format(TIME_FORMAT)}:00`
              } else if (type_id === 3) {
                // Build payload shape for type 3
                // from_date/to_date now come from from_date & to_date fields
                if (fieldsValue.from_date) {
                  values.from_date = fieldsValue.from_date.format(DATE_FORMAT)
                }
                if (fieldsValue.to_date) {
                  values.to_date = fieldsValue.to_date.format(DATE_FORMAT)
                }

                // Purpose
                if (fieldsValue.tour_purpose_id) {
                  values.tour_purpose_id = fieldsValue.tour_purpose_id
                }

                // Extract tour_plans from dynamic day fields
                const plansByPrefix = {}
                Object.keys(fieldsValue).forEach((key) => {
                  const match = key.match(/^day_(\d+)_([0-9-]+)_(location|planned_activity|transport|description)$/)
                  if (match) {
                    const [, dayIndex, dayDate, field] = match
                    const prefix = `day_${dayIndex}_${dayDate}`
                    if (!plansByPrefix[prefix]) plansByPrefix[prefix] = {}
                    plansByPrefix[prefix][field] = fieldsValue[key]
                  }
                })

                const planList = Object.entries(plansByPrefix).map(([prefix, p]) => {
                  const idKey = `${prefix}_id`
                  let id = fieldsValue[idKey]
                  if (!id && editItem?.tour_plans?.length) {
                    const m = prefix.match(/^day_(\d+)_([0-9-]+)$/)
                    const dateYMD = m ? m[2] : undefined
                    if (dateYMD) {
                      const matched = editItem.tour_plans.find((pl) => moment(pl.date).format('YYYY-MM-DD') === dateYMD)
                      if (matched) id = matched.id
                    }
                  }
                  const dm = prefix.match(/^day_(\d+)_([0-9-]+)$/)
                  const planDateYMD = dm ? dm[2] : undefined
                  return {
                    ...(id ? { id } : {}),
                    ...(planDateYMD ? { date: planDateYMD } : {}),
                    location: p.location || '',
                    planned_activity_ids: Array.isArray(p.planned_activity) ? p.planned_activity : (p.planned_activity ? [p.planned_activity] : []),
                    transport_id: p.transport,
                    note: p.description || '',
                  }
                })
                if (planList.length > 0) {
                  values.tour_plans = planList
                }
                console.log(planList, "values")

                // Clean up any old structure keys if present
                delete values.day_planning
                delete values.activity_type

                // Remove raw day_*_*_* fields from values after building tour_plans
                Object.keys(values).forEach((key) => {
                  if (/^day_(\d+)_([0-9-]+)_(id|date|location|planned_activity|transport|description)$/.test(key)) {
                    delete values[key]
                  }
                })
              } else {
                // Other types: use from/to date pickers
                if (fieldsValue.from) {
                  values.from_date = fieldsValue.from.format(DATE_FORMAT)
                }
                if (fieldsValue.to) {
                  values.to_date = fieldsValue.to.format(DATE_FORMAT)
                }
              }
              if (editItem) values.id = editItem.id
              return dispatch(addOrUpdateRemoteWork(values))
            },
            title: !editItem ? 'Remote work application' : 'Update application',
            form,
            allfieldsData,
          }}
        />
      )}
      {viewVisible && (
        <ViewModal
          visible={viewVisible}
          handleCancel={() => setViewVisible(false)}
          viewItem={viewItem}
          allfieldsData={allfieldsData}
          fullListData={data}
          onEdit={(item) => handleEdit(item)}
          onDelete={(item) => handleViewDelete(item)}
          onSubmit={(item) => handleActions(item, 'SUBMIT')}
          onApprove={(item) => handleActions(item, 'APPROVE')}
          onReject={(item) => handleActions(item, 'REJECT')}
          listType={listType}
        />
      )}
    </Auxiliary>
  )
}

export default RemoteWork
